import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { listClaims } from "../../api/clientVendorDirectoryClaimApi";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.claims";

const STATUS_FILTERS = [
  "",
  "submitted",
  "pending_contact_verification",
  "pending_evidence",
  "pending_review",
  "awaiting_information",
  "approved",
  "rejected",
  "disputed",
];

const RISK_FILTERS = ["", "low", "medium", "high"];

const PENDING_STATUSES = new Set([
  "submitted",
  "pending_contact_verification",
  "pending_evidence",
  "pending_review",
  "awaiting_information",
]);

function statusBadgeClass(status) {
  if (status === "approved") return "cvd-badge--published";
  if (["rejected", "revoked", "expired"].includes(status)) return "cvd-badge--suspended";
  if (["pending_review", "awaiting_information", "disputed"].includes(status)) return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function riskBadgeClass(risk) {
  if (risk === "high") return "cvd-badge--suspended";
  if (risk === "medium") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function formatDateTime(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function MetricCard({ label, value }) {
  return (
    <article className="cvd-metric-card">
      <span className="cvd-metric-card__label">{label}</span>
      <strong className="cvd-metric-card__value">{value ?? 0}</strong>
    </article>
  );
}

export default function ClientVendorDirectoryClaimListPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t, locale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const status = searchParams.get("status") ?? "pending_review";
  const riskLevel = searchParams.get("riskLevel") || "";
  const basePath = `${serviciosBase}/reclamaciones`;
  const viewName = activeView?.name || "CasaMapa";

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listClaims(clientViewId, {
        status: status || undefined,
        riskLevel: riskLevel || undefined,
      });
      setClaims(body.claims || []);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
      setClaims([]);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, riskLevel, status, t]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const pendingReview = claims.filter((claim) => PENDING_STATUSES.has(claim.status)).length;
    const highRisk = claims.filter((claim) => claim.riskLevel === "high").length;
    const verifiedContact = claims.filter((claim) => claim.contactVerifiedAt).length;
    return { total: claims.length, pendingReview, highRisk, verifiedContact };
  }, [claims]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="page-content cvd-admin cvd-claims">
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.title`)}</h1>
          <p className="lede">{t(`${COPY_PREFIX}.intro`, { viewName })}</p>
        </div>
        <div className="cvd-admin__actions">
          <button type="button" className="cvd-btn cvd-btn--ghost" onClick={load}>
            {t(`${COPY_PREFIX}.refresh`)}
          </button>
        </div>
      </div>

      {!loading && claims.length > 0 ? (
        <div className="cvd-metric-grid">
          <MetricCard label={t(`${COPY_PREFIX}.kpi.total`)} value={summary.total} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.pendingReview`)} value={summary.pendingReview} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.highRisk`)} value={summary.highRisk} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.verifiedContact`)} value={summary.verifiedContact} />
        </div>
      ) : null}

      <div className="cvd-duplicate-filters">
        <div className="cvd-duplicate-filters__group">
          <span className="cvd-duplicate-filters__label">{t(`${COPY_PREFIX}.filterStatus`)}</span>
          <div className="cvd-run-filters" role="group" aria-label={t(`${COPY_PREFIX}.filterStatus`)}>
            {STATUS_FILTERS.map((value) => (
              <button
                key={value || "all-status"}
                type="button"
                className={`cvd-run-filter-chip${status === value ? " is-active" : ""}`}
                onClick={() => updateParam("status", value)}
              >
                {value ? t(`${COPY_PREFIX}.status.${value}`) : t(`${COPY_PREFIX}.allStatuses`)}
              </button>
            ))}
          </div>
        </div>

        <div className="cvd-duplicate-filters__group">
          <span className="cvd-duplicate-filters__label">{t(`${COPY_PREFIX}.filterRisk`)}</span>
          <div className="cvd-run-filters" role="group" aria-label={t(`${COPY_PREFIX}.filterRisk`)}>
            {RISK_FILTERS.map((value) => (
              <button
                key={value || "all-risk"}
                type="button"
                className={`cvd-run-filter-chip${riskLevel === value ? " is-active" : ""}`}
                onClick={() => updateParam("riskLevel", value)}
              >
                {value ? t(`${COPY_PREFIX}.risk.${value}`) : t(`${COPY_PREFIX}.allRisk`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} /> : null}
      {!loading && errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}

      {!loading && !errorMessage && claims.length === 0 ? (
        <section className="cvd-run-empty">
          <h2>{t(`${COPY_PREFIX}.emptyTitle`)}</h2>
          <p>{t(`${COPY_PREFIX}.emptyHint`)}</p>
        </section>
      ) : null}

      {!loading && !errorMessage && claims.length > 0 ? (
        <div className="cvd-run-list">
          {claims.map((claim) => (
            <article key={claim.id} className="cvd-run-card cvd-claim-card">
              <div className="cvd-run-card__main">
                <div className="cvd-run-card__head">
                  <div>
                    <h2 className="cvd-claim-card__vendor">{claim.vendorDisplayName || "—"}</h2>
                    <p className="cvd-run-card__sub">
                      {claim.claimantAccountEmail || claim.claimantEmail || "—"} ·{" "}
                      {t(`${COPY_PREFIX}.relationship.${claim.relationshipToVendor}`, {}, claim.relationshipToVendor)}
                    </p>
                  </div>
                  <div className="cvd-claim-card__badges">
                    <span className={`cvd-badge ${statusBadgeClass(claim.status)}`}>
                      {t(`${COPY_PREFIX}.status.${claim.status}`, {}, claim.status)}
                    </span>
                    {claim.riskLevel ? (
                      <span className={`cvd-badge ${riskBadgeClass(claim.riskLevel)}`}>
                        {t(`${COPY_PREFIX}.risk.${claim.riskLevel}`, {}, claim.riskLevel)}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="cvd-run-card__stats">
                  <span>
                    {t(`${COPY_PREFIX}.table.verification`)}:{" "}
                    {claim.contactVerifiedAt
                      ? t(`${COPY_PREFIX}.contactVerified`)
                      : t(`${COPY_PREFIX}.contactNotVerified`)}
                  </span>
                  <span>
                    {t(`${COPY_PREFIX}.table.evidence`)}: <strong>{claim.evidenceCount ?? 0}</strong>
                  </span>
                  <span>
                    {t(`${COPY_PREFIX}.table.submitted`)}: {formatDateTime(claim.submittedAt || claim.createdAt, locale)}
                  </span>
                </div>
              </div>

              <div className="cvd-run-card__actions">
                <Link className="cvd-btn cvd-btn--primary" to={`${basePath}/${claim.id}`}>
                  {t(`${COPY_PREFIX}.review`)}
                </Link>
              </div>
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
}
